import React from 'react';
import ScreenGallery from './ScreenGallery';
import SCREENS from '../data/screens';
import {
  LayoutDashboard, PhoneCall, Users, UsersRound, Contact, Megaphone,
  ListChecks, MessageSquare, CalendarCheck, BarChart3, LifeBuoy,
  SlidersHorizontal, GitBranch, FileText, TrendingUp, PhoneOff,
  ClipboardCheck, Zap, CheckSquare, Globe,
} from 'lucide-react';

/**
 * The module reference: one entry per screen of the product, each with the
 * real interface, what the screen is for, and what you can do on it.
 *
 * Screenshots are captured from the running app and live in /public/docs.
 * Mobile numbers are masked at capture time; nothing else in the records is
 * altered, so what you see is the real interface with real data shapes.
 */
export const MODULES = [
  {
    id: 'mod-dashboard',
    icon: LayoutDashboard,
    accent: 'accent-1',
    name: 'Dashboard',
    who: 'Admins, managers, team leads',
    shot: '/docs/dashboard.webp',
    summary:
      'The opening screen. It answers “how is today going?” in one view — money collected, calls placed, customers closed — and lets you switch the whole page between today, this week and this month.',
    features: [
      'Headline tiles for payments collected, total calls and customers closed',
      'Conversion rates: opportunity win rate, task completion and call connect rate',
      'Created vs closed comparison for opportunities and tasks side by side',
      'Connected and missed call gauges with live percentages',
      'Outreach counters for talk time, SMS, email and web calls',
      'Agent performance table with availability, split by who is online and offline',
    ],
  },
  {
    id: 'mod-sip-call',
    icon: PhoneCall,
    accent: 'accent-6',
    name: 'Agent dialer (SIP call)',
    who: 'Agents',
    shot: '/docs/sip-call.webp',
    summary:
      'The screen an agent works in all day. Pick a campaign, set yourself available, and dial — by hand from the keypad or hands-free with auto-dial working down the campaign list.',
    features: [
      'On-screen keypad plus one-tap dialling straight from the campaign list',
      'Campaign picker and availability status the supervisor sees live',
      'Auto-dial mode that walks the assigned customer list for you',
      'Customer lookup that pulls the record up as the number is entered',
      'Tabs for call log, personal performance and follow-ups without leaving the dialer',
      'Running counters for calls placed and calls connected in the session',
    ],
  },
  {
    id: 'mod-users',
    icon: Users,
    accent: 'accent-5',
    name: 'Users',
    who: 'Admins',
    shot: '/docs/users.webp',
    summary:
      'Everyone who can sign in. Add agents, managers and admins, set what each one is allowed to do, and see at a glance how many seats the plan has left.',
    features: [
      'Add users one at a time or bulk-upload the whole team from a sheet',
      'Roles: agent, manager, admin and superadmin, each with its own permissions',
      'Assign managers so every agent reports to someone',
      'Attach skills, so campaigns can route work to the right people',
      'Search by name, email or mobile, and filter by role or active status',
      'Seat counter against your plan, and a location pin where sharing is enabled',
    ],
  },
  {
    id: 'mod-teams',
    icon: UsersRound,
    accent: 'accent-4',
    name: 'Teams & skills',
    who: 'Admins, managers',
    shot: '/docs/teams.webp',
    summary:
      'Group agents the way your floor actually works — by product, language, shift or region — and describe what each group is good at, so campaigns can be pointed at a team instead of a list of names.',
    features: [
      'Create teams and move agents between them',
      'Name a team lead who sees that team’s numbers',
      'Define skills once and attach them to people and campaigns',
      'Assign work by team or by skill rather than person by person',
    ],
  },
  {
    id: 'mod-customers',
    icon: Contact,
    accent: 'accent-3',
    name: 'Customers & leads',
    who: 'Admins, managers, agents',
    shot: '/docs/customers.webp',
    summary:
      'Your contact database. Import a lead list, tag where each record came from, assign records to agents, and keep the do-not-call list honoured automatically.',
    features: [
      'Bulk upload from CSV, with column matching and a duplicate check',
      'Assign agents in bulk, or let a campaign distribute the list',
      'Lead sources tracked per record — web, Facebook, Instagram, LinkedIn, WhatsApp and more',
      'UTM source, medium, campaign and content captured for anything arriving from an ad',
      'Do-not-call flag that takes a number out of dialling everywhere',
      'Filter by group, source, campaign, status, opportunity stage and created date',
      'Export the filtered view back out as a file',
    ],
  },
  {
    id: 'mod-campaigns',
    icon: Megaphone,
    accent: 'accent-8',
    name: 'Campaigns',
    who: 'Admins, managers',
    shot: '/docs/campaigns.webp',
    summary:
      'A campaign is a piece of work: a list of customers, the people who will work it, and the channels they may use. Several can run side by side without interfering.',
    features: [
      'Voice, SMS, email and chat channels selectable per campaign',
      'Attach users, whole teams, or a required skill',
      'Priority ordering, so agents get the important campaign first',
      'Active and inactive states — pause a campaign without deleting anything',
      'Per-campaign customer list, feeding the agent dialer directly',
      'Search and filter by status across every campaign',
    ],
  },
  {
    id: 'mod-tasks',
    icon: ListChecks,
    accent: 'accent-5',
    name: 'Tasks & pipeline',
    who: 'Agents, managers',
    shot: '/docs/tasks.webp',
    summary:
      'Every call, callback and follow-up as a card on a board. Drag a card to move the work forward; the board can be read by priority or by sales stage.',
    features: [
      'Drag-and-drop board across workflow stages',
      'Two views of the same work: priority-wise, or by sales stage',
      'Columns for opportunity tasks, open, closed and due today',
      'Cards carry type (call or message), outcome, customer, campaign, dates and assignee',
      'Filter by campaign, type, assignee, customer, status and date range',
      'Task statuses are yours to define in configuration',
    ],
  },
  {
    id: 'mod-messages',
    icon: MessageSquare,
    accent: 'accent-4',
    name: 'Messages',
    who: 'Agents, managers',
    shot: '/docs/messages.webp',
    summary:
      'Outbound conversations in one thread view — WhatsApp, SMS and email — so the written follow-up sits next to the calls rather than in someone’s personal phone.',
    features: [
      'One place for WhatsApp, SMS and email conversations',
      'Send from a saved template so the wording stays consistent',
      'Message history attached to the customer record',
      'Delivery state visible on the thread',
    ],
  },
  {
    id: 'mod-templates',
    icon: FileText,
    accent: 'accent-7',
    name: 'Message templates',
    who: 'Admins, managers',
    shot: '/docs/templates.webp',
    summary:
      'Pre-approved wording your team can send without retyping it — and without going off-message. Written once, used by everyone.',
    features: [
      'Templates per channel: WhatsApp, SMS and email',
      'Placeholders that fill in customer details when sent',
      'Previews that show the message as the customer will see it',
      'Edit centrally — every agent picks up the change immediately',
    ],
  },
  {
    id: 'mod-opportunities',
    icon: TrendingUp,
    accent: 'accent-2',
    name: 'Opportunities',
    who: 'Agents, managers',
    summary:
      'The pipeline that builds itself out of calling. An agent can open an opportunity the moment a call ends, without leaving the dialer, and managers see value and win rate roll up per campaign.',
    features: [
      'Post-call submission straight from the dialer',
      'Configurable stages, values and close reasons',
      'Bulk assignment across agents and teams',
      'Won and lost tracked with a reason on every record',
      'Pipeline value and win rate in the reports module',
    ],
  },
  {
    id: 'mod-auto-dial',
    icon: Zap,
    accent: 'accent-7',
    name: 'Auto dial',
    who: 'Agents',
    summary:
      'A server-side queue agents claim from, one record at a time. It respects each campaign’s calling window and hands a number to a single agent, so two people never dial the same lead.',
    features: [
      'Queue claimed one record at a time, per agent',
      'Campaign calling windows honoured automatically',
      'Release and requeue when an agent steps away',
      'No double-dialling across a floor',
      'Runs alongside manual dialling in the same session',
    ],
  },
  {
    id: 'mod-dnc',
    icon: PhoneOff,
    accent: 'accent-4',
    name: 'Do Not Call',
    who: 'Admins, compliance',
    summary:
      'The suppression list. Numbers here are checked before anything reaches an agent, so a DNC record stops the call rather than warning about it afterwards.',
    features: [
      'DNC groups managed apart from lead lists',
      'Bulk upload and bulk delete, with a sample file',
      'Full registry export for audit',
      'Applied across campaigns and the auto dial queue',
      'Do-not-call flag visible on the customer record',
    ],
  },
  {
    id: 'mod-assessments',
    icon: ClipboardCheck,
    accent: 'accent-5',
    name: 'Assessments',
    who: 'Managers, trainers',
    shot: '/docs/screens/assessments/page.webp',
    summary:
      'Question sets shared by link, with their own timed sessions. Used for agent certification and for qualifying leads — answers can include uploaded files, and results roll up per assessment.',
    features: [
      'Shareable links, each attempt its own session',
      'Several question types, including file-upload answers',
      'Timed attempts with per-question progress',
      'Scoring and outcome rules configured per assessment',
      'Results view per assessment and per participant',
    ],
  },
  {
    id: 'mod-approvals',
    icon: CheckSquare,
    accent: 'accent-3',
    name: 'Approvals & leave',
    who: 'Managers, admins',
    shot: '/docs/screens/approvals-leaves/page.webp',
    summary:
      'Where leave requests land. Managers work a pending queue and approve or reject, and the outcome feeds straight into attendance so the floor plan stays accurate.',
    features: [
      'Pending, approved, rejected and all views',
      'Leave types defined per organisation',
      'Approval trail kept on every request',
      'Feeds the attendance records and reports',
    ],
  },
  {
    id: 'mod-webforms',
    icon: Globe,
    accent: 'accent-6',
    name: 'Web forms & web calls',
    who: 'Admins, marketing',
    summary:
      'Lead capture from your own site. An embedded form drops answers straight into Customers with source attribution intact, and a web call puts a browser visitor into the same agent queue as everything else.',
    features: [
      'Embeddable forms, each with its own token',
      'Session-based answers with per-question skip logic',
      'Source, medium and campaign attributed on arrival',
      'Browser-initiated calls into the normal agent queue',
      'No separate inbox — everything lands in Customers',
    ],
  },
  {
    id: 'mod-attendance',
    icon: CalendarCheck,
    accent: 'accent-6',
    name: 'Attendance & leave',
    who: 'Admins, managers, agents',
    shot: '/docs/attendance.webp',
    summary:
      'Shift tracking for the floor: who clocked in, when, from where, and who is on approved leave — with the approval queue in the same module.',
    features: [
      'Attendance records per agent, per day',
      'Leave requests with an approvals queue for managers',
      'Attendance rules configurable per organisation',
      'Feeds the productivity and attendance report',
    ],
  },
  {
    id: 'mod-reports',
    icon: BarChart3,
    accent: 'accent-1',
    name: 'Reports',
    who: 'Admins, managers',
    shot: '/docs/reports.webp',
    summary:
      'The measurement screen. Pick a date range and a granularity, then read the same period across six different lenses.',
    features: [
      'Tabs for overview, agents, customers, communications, sales & payments, and productivity & attendance',
      'Date range with daily, weekly or monthly granularity, filtered to one agent or all',
      'Headline tiles: total calls, conversion, opportunities, tasks, payments and messages',
      'Activity trend over time, with calls, connected calls and opportunities won',
      'Payments collected across the period',
      'Calls by top agent, ranked',
    ],
  },
  {
    id: 'mod-support',
    icon: LifeBuoy,
    accent: 'accent-2',
    name: 'Support tickets',
    who: 'Everyone',
    shot: '/docs/support.webp',
    summary:
      'An internal help desk. When something is wrong — a number that will not dial, a record that looks incorrect — it is raised here and tracked to closure instead of living in a chat message.',
    features: [
      'Raise a ticket against an issue type',
      'Issue types are configurable to match how your organisation triages',
      'Status tracked from raised to resolved',
      'Visible to the people who need to act on it',
    ],
  },
  {
    id: 'mod-custom-fields',
    icon: SlidersHorizontal,
    accent: 'accent-7',
    name: 'Custom fields',
    who: 'Admins',
    shot: '/docs/custom-fields.webp',
    summary:
      'The part that makes WASLX fit your business rather than the other way round. Add the fields your process needs — policy number, property type, loan amount — and they appear on the record and in exports.',
    features: [
      'Add fields to customer and lead records',
      'Several field types, so a date behaves like a date',
      'Fields show up in the app, in filters and in exports',
      'No developer needed — an admin can do it',
    ],
  },
  {
    id: 'mod-ivr',
    icon: GitBranch,
    accent: 'accent-5',
    name: 'IVR flows & numbers',
    who: 'Admins',
    shot: '/docs/ivr-flows.webp',
    summary:
      'What happens when somebody calls you. Build the menu — press 1 for sales, 2 for support — and attach it to the numbers you have published.',
    features: [
      'Build inbound menus step by step',
      'Route each option to a team, a skill or a specific agent',
      'Attach a flow to a DID (an inbound number) so published numbers behave correctly',
      'Manage SIP trunks and DID numbers alongside the flows',
    ],
  },
];


/* Capture slugs per module. The manual groups screens the way a reader
   thinks about them ("Teams & skills" is one entry), while the capture pass
   is keyed by route, so several modules pull from more than one slug. Any
   slug with no captures is simply skipped. */
export const MODULE_SCREENS = {
  'mod-dashboard':     ['dashboard'],
  'mod-sip-call':      ['sip-call'],
  'mod-users':         ['users'],
  'mod-teams':         ['teams', 'skills'],
  'mod-customers':     ['customers', 'customers-groups', 'customers-sources'],
  'mod-campaigns':     ['campaigns'],
  'mod-assessments':   ['assessments', 'assessments-detail', 'assessments-detailresults'],
  'mod-approvals':     ['approvals-leaves'],
  'mod-tasks':         ['tasks', 'task-statuses'],
  'mod-messages':      ['messages', 'notifications'],
  'mod-opportunities': [],
  'mod-auto-dial':     [],
  'mod-dnc':           [],
  'mod-webforms':      [],
  'mod-templates':     ['customers-templates'],
  'mod-attendance':    ['attendance-records', 'configuration-attendance'],
  'mod-reports':       ['reports'],
  'mod-support':       ['support', 'support-issue-types'],
  'mod-custom-fields': ['configuration-custom-fields', 'profile'],
  'mod-ivr':           ['configuration-ivr-flows', 'configuration-sip-trunks', 'configuration-dids'],
};

const screensFor = id => (MODULE_SCREENS[id] || []).flatMap(slug => SCREENS[slug] || []);

/**
 * One module: the interface, what it is for, and what you can do on it.
 *
 * This was a vertical stack — heading, prose, a full-measure screenshot,
 * then the capability list — repeated fourteen times. Two things were wrong
 * with it. The page opens by promising "the real interface BESIDE every
 * explanation", and a stacked shot is below it, not beside it; you cannot
 * read what a screen does and look at that screen at the same time, which
 * is the entire point of an illustrated manual. And fourteen identical
 * stacks with a 1400px-wide image in each made the section scroll for
 * roughly thirty thousand pixels, so nothing was ever on screen with
 * anything else.
 *
 * It is a bounded panel now, split: what the module is on the left, what it
 * looks like on the right. The two columns sit at the same top edge, so the
 * summary and the screen it describes are genuinely level with each other.
 * The capability list lost its .card--tint box on the way — a card inside a
 * card is a border for its own sake, and the panel already bounds it.
 */
const ModuleCard = ({ module }) => {
  const Icon = module.icon;
  const gallery = screensFor(module.id);
  /* Modules documented after the capture pass have no hand-picked hero, so
     fall back to their first capture. Some have neither yet — those drop the
     visual column entirely rather than leaving half the panel empty. */
  const hero = module.shot || gallery[0]?.src || null;
  const hasVisual = Boolean(hero || gallery.length);

  return (
    <article id={module.id} className={`${module.accent} doc-module scroll-mt-24`}>
      <header className="doc-module__head">
        <span className="icon-tile">
          <Icon className="h-5 w-5" />
        </span>

        <div className="min-w-0 flex-1">
          <h3 className="doc-module__title">{module.name}</h3>
          <p className="doc-module__who">Used by: {module.who}</p>
        </div>

        {gallery.length > 0 && (
          <span className="doc-module__count">
            {gallery.length}{' '}
            <span className="doc-module__count-unit">
              {gallery.length === 1 ? 'screen' : 'screens'}
            </span>
          </span>
        )}
      </header>

      <div className={`doc-module__body ${hasVisual ? '' : 'doc-module__body--solo'}`}>
        <div className="doc-module__copy">
          <p className="leading-relaxed text-content-muted">{module.summary}</p>

          <p className="doc-module__label">What you can do here</p>
          {/* Hairlines, not a tinted box: this list is already inside a
              bounded panel, and a second border around it adds nothing. */}
          <ul className="doc-module__list">
            {module.features.map(feature => (
              <li key={feature}>
                <span className="doc-module__bullet" aria-hidden="true" />
                {feature}
              </li>
            ))}
          </ul>
        </div>

        {hasVisual && (
        <div className="doc-module__visual">
          {hero && (
            <figure className="doc-module__hero">
              <img
                src={hero}
                alt={`The WASLX ${module.name} screen`}
                loading="lazy"
                width="1400"
                height="840"
              />
            </figure>
          )}

          {gallery.length > 0 && (
            <>
              <p className="doc-module__label doc-module__label--tight">
                Every screen in this module
                <span className="doc-module__hint">click to enlarge</span>
              </p>
              <ScreenGallery screens={gallery} title={module.name} />
            </>
          )}
        </div>
        )}
      </div>
    </article>
  );
};

const ModuleShowcase = () => (
  <div className="doc-module-stack">
    {MODULES.map(module => (
      <ModuleCard key={module.id} module={module} />
    ))}
  </div>
);

export default ModuleShowcase;
